import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { fetchCollections, fetchProducts, DISCLAIMER } from "../lib/api";

export default function Shop() {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("collection") || (params.get("type") ? `type:${params.get("type")}` : "all");

  useEffect(() => {
    fetchCollections().then(setCollections);
    fetchProducts().then(setProducts);
  }, []);

  const filters = useMemo(
    () => [
      { key: "all", label: "All" },
      ...collections.map((c) => ({ key: c.slug, label: c.name })),
      { key: "type:salve", label: "Salves" },
      { key: "type:other", label: "Other" },
      { key: "type:bundle", label: "Bundles" },
    ],
    [collections]
  );

  const shown = useMemo(() => {
    if (active === "all") return products;
    if (active.startsWith("type:")) return products.filter((p) => p.type === active.split(":")[1]);
    return products.filter((p) => p.collection === active);
  }, [products, active]);

  const setFilter = (key) => {
    if (key === "all") return setParams({});
    if (key.startsWith("type:")) return setParams({ type: key.split(":")[1] });
    setParams({ collection: key });
  };

  return (
    <div className="pt-32 md:pt-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-5">The Shop</p>
          <h1 className="font-serif text-5xl md:text-7xl text-forest tracking-tight leading-none mb-8">
            Blended by <span className="italic text-sage">intention</span>.
          </h1>
        </Reveal>

        <div className="flex flex-wrap gap-2 mb-14" data-testid="shop-filters">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key.replace(/[:]/g, "-")}`}
              className={`rounded-full px-5 py-2.5 text-sm border ${
                active === f.key ? "bg-forest text-cream border-forest" : "text-forest border-forest/15 hover:border-forest/40"
              }`}
              style={{ transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease" }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12" data-testid="shop-grid">
          {shown.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>

        {shown.length === 0 && <p className="text-forest-soft py-20 text-center">Loading blends…</p>}

        <div className="mt-20 rounded-2xl bg-surface p-6">
          <p className="text-xs leading-relaxed text-forest-soft">{DISCLAIMER}</p>
        </div>
      </div>
    </div>
  );
}
